import React from 'react';
import { Award, GraduationCap, Star, Clock, BookOpen } from 'lucide-react';
import './FounderPage.css';

function FounderPage({ onSelectCourse, onNavigateToContact }) {
  const featuredPrograms = [
    {
      id: 2,
      title: "Future Pathways",
      author: "By V. Swaminathan",
      target: "Class 9–10",
      category: "School Students",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      desc: "Future Pathways helps Class 9–10 students understand their strengths, interests and aspirations, explore suitable career directions, and build clarity before making important stream and subject choices.",
      image: "/images/Home/Course2.jpeg"
    },
    {
      id: 3,
      slug: "career-discovery",
      title: "Career Discovery",
      author: "By V. Swaminathan",
      target: "Class 11–12",
      category: "School Students",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      desc: "Career Discovery helps Class 11–12 students understand themselves, explore right-fit career pathways and make confident decisions about their future. Through structured self-discovery, scientific assessment and guided career research, students build clarity before choosing their next step.",
      image: "/images/Home/Course3.jpeg"
    },
    {
      id: 5,
      slug: "career-launch",
      title: "Career Launch",
      author: "By V. Swaminathan",
      target: "Undergraduate Students",
      category: "Higher Education",
      rating: "4.9",
      duration: "Personalised Coaching",
      lessons: "5 Modules",
      desc: "Career Launch helps undergraduate students understand their strengths, interests and career goals, explore suitable career pathways and prepare for a confident transition from college to the world of work.",
      image: "/images/Home/Course4.jpeg"
    }
  ];

  return (
    <div className="founder-page padding-y">
      <div className="container">

        {/* ================= SECTION 1: STICKY LEFT PROFILE & RIGHT BIOGRAPHY ================= */}
        <div className="founder-hero-grid">

          {/* Left Column: Sticky Profile Card */}
          <div className="founder-left-sticky">
            <div className="founder-profile-card">
              <div className="founder-img-wrapper">
                <img
                  src="/images/Home/Founder.png"
                  alt="V. Swaminathan - Founder"
                  className="founder-card-img"
                />
              </div>

              <div className="founder-profile-info">
                <h3 className="founder-card-name">V. Swaminathan</h3>
                <p className="founder-card-role">Founder, Deepclariti™</p>
                <p className="founder-card-subrole">Internationally Certified Career Coach</p>

                <div className="founder-social-pills">
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-pill" aria-label="Facebook">
                    <span className="social-icon-symbol social-fb">f</span>
                  </a>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-pill" aria-label="Instagram">
                    <span className="social-icon-symbol social-ig">📷</span>
                  </a>
                  <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="social-pill" aria-label="X">
                    <span className="social-icon-symbol">X</span>
                  </a>
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-pill" aria-label="LinkedIn">
                    <span className="social-icon-symbol social-in">in</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Detailed Biography & Experience */}
          <div className="founder-right-content">

            {/* About V. Swaminathan */}
            <div className="founder-content-block">
              <h2 className="founder-section-title">About V. Swaminathan</h2>
              <p className="founder-paragraph">
                Hi, I'm V. Swaminathan, Founder of Deepclariti™ and an Internationally Certified Career Coach with over 25 years of experience in Indian and international higher education.
              </p>
              <p className="founder-paragraph">
                My work focuses on helping students understand their strengths, interests, personality, motivations and aspirations before making important education and career decisions.
              </p>
              <p className="founder-paragraph">
                Over the years, I have personally coached 10,000+ students and trained 350+ career advisors. This experience led me to create the Deepclariti™ Career Coaching Framework — a structured approach that helps students move from self-discovery to clarity, confidence and informed career direction.
              </p>
              <p className="founder-paragraph">
                My approach is not about telling students what career to choose. It is about helping them understand themselves, explore suitable possibilities and take ownership of the decisions that shape their future.
              </p>
            </div>

            {/* My Coaching Philosophy */}
            <div className="founder-content-block">
              <h2 className="founder-section-title">My Coaching Philosophy</h2>
              <p className="founder-paragraph">
                Career clarity begins with understanding yourself — not with choosing a course, college or career.
              </p>
              <p className="founder-paragraph">
                I believe students make stronger decisions when they understand their strengths, interests, personality, motivations, values and aspirations. Through structured self-discovery, reflective questioning and scientifically validated assessments, I help students explore possibilities without pressure.
              </p>
              <p className="founder-paragraph">
                The goal is not to prescribe a career. It is to help each student develop the clarity, confidence and ownership to make decisions they genuinely believe in.
              </p>
            </div>

            {/* Experience & Credentials */}
            <div className="founder-content-block">
              <h2 className="founder-section-title">Experience &amp; Credentials</h2>
              
              <div className="experience-highlights-list">

                {/* Point 1 */}
                <div className="highlight-item">
                  <div className="highlight-icon-box">
                    <Award size={20} className="highlight-icon" />
                  </div>
                  <div className="highlight-details">
                    <p className="highlight-lead">
                      <strong>25+ Years of Experience</strong>
                    </p>
                    <p className="highlight-subtext">
                      Experience across Indian and international higher education, student mentoring and career coaching.
                    </p>
                  </div>
                </div>

                {/* Point 2 */}
                <div className="highlight-item">
                  <div className="highlight-icon-box">
                    <Award size={20} className="highlight-icon" />
                  </div>
                  <div className="highlight-details">
                    <p className="highlight-lead">
                      <strong>10,000+ Students Coached</strong>
                    </p>
                    <p className="highlight-subtext">
                      Personally guided students across different stages of education and career decision-making.
                    </p>
                  </div>
                </div>

                {/* Point 3 */}
                <div className="highlight-item">
                  <div className="highlight-icon-box">
                    <Award size={20} className="highlight-icon" />
                  </div>
                  <div className="highlight-details">
                    <p className="highlight-lead">
                      <strong>350+ Career Advisors Trained</strong>
                    </p>
                    <p className="highlight-subtext">
                      Trained career advisors to strengthen professional career guidance practices.
                    </p>
                  </div>
                </div>

                {/* Point 4 */}
                <div className="highlight-item">
                  <div className="highlight-icon-box">
                    <GraduationCap size={20} className="highlight-icon" />
                  </div>
                  <div className="highlight-details">
                    <p className="highlight-lead">
                      <strong>Internationally Certified Career Coach</strong>
                    </p>
                    <p className="highlight-subtext">
                      Internationally certified, with certification from the National Career Development Association (NCDA), USA.
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* Beyond Career Coaching */}
            <div className="founder-content-block">
              <h2 className="founder-section-title">Beyond Career Coaching</h2>
              <p className="founder-paragraph">
                Alongside one-to-one coaching, I work with students and parents to understand emerging industries, future-ready skills and changing career opportunities.
              </p>
              <p className="founder-paragraph">
                My aim is to help families look beyond short-term trends and make thoughtful decisions based on the student's strengths, aspirations and long-term possibilities.
              </p>
            </div>

          </div>

        </div>

        {/* ================= SECTION 2: V. SWAMINATHAN ALL PROGRAMS ================= */}
        <div className="founder-programs-section">
          <h2 className="founder-programs-title">
            <span className="title-highlight-italic">A Journey Built Around</span> Student Success
          </h2>

          <div className="founder-programs-grid">
            {featuredPrograms.map((course) => (
              <div key={course.id} className="course-card">
                <div className="course-img-wrapper">
                  <img src={course.image} alt={course.title} className="course-img" />
                </div>

                <div className="course-meta-top">
                  <div className="rating-box">
                    <div className="stars-group">
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                      <Star size={13} fill="#F59E0B" stroke="none" />
                    </div>
                    <span className="rating-score">({course.rating})</span>
                  </div>
                </div>

                <h3 className="course-card-title">{course.title}</h3>
                <p className="course-target-label">{course.author}</p>

                <div className="course-details-row">
                  <div className="course-detail-item">
                    <Clock size={14} />
                    <span>{course.duration}</span>
                  </div>
                  <div className="course-detail-item">
                    <BookOpen size={14} />
                    <span>{course.lessons}</span>
                  </div>
                </div>

                <button
                  onClick={() => onSelectCourse && onSelectCourse(course)}
                  className="btn-view-course"
                >
                  View Program
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ================= SECTION 3: KICKSTART BANNER ================= */}
        <div className="founder-kickstart-banner">
          <div className="kickstart-badge-pill">
            READY TO TAKE THE NEXT STEP?
          </div>

          <h2 className="kickstart-banner-title">
            The Right Guidance Can Change<br />
            a Student’s Entire Future.
          </h2>

          <p className="kickstart-banner-desc">
            Whether you are a student searching for direction or a parent seeking clarity, Deepclariti™ helps you make informed and confident decisions about the road ahead.
          </p>

          <button
            onClick={onNavigateToContact}
            className="btn-kickstart-action"
          >
            Schedule a Consultation
          </button>
        </div>

      </div>
    </div>
  );
}

export default FounderPage;
